import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

type Accent = "blue" | "violet";

interface Moat {
  num: string;
  title: string;
  strap: string;
  body: ReactNode;
  proof?: string;
}

const NOW_MOATS: Moat[] = [
  {
    num: "1",
    title: "Methodology Head Start",
    strap: "Human ASO expertise compiled into continuously-tuned software.",
    body: (
      <>
        Hundreds of expert prompts, scoring rubrics, multi-model
        cross-validation, and calibration harnesses<SupLink to="platform-depth">B</SupLink>{" "}
        — running daily, observed, continuously tuned:{" "}
        <span className="font-semibold text-slate-900">
          109 methodology updates in the last 180 days.
        </span>{" "}
        The expertise lives in the running system, not in any one person.
      </>
    ),
    proof:
      "Proof it works: the only platform in production auditing how apps surface in ChatGPT, Claude, Gemini & Perplexity answers.",
  },
  {
    num: "2",
    title: "Technology Head Start",
    strap: "Every pipe in production, end-to-end.",
    body: (
      <>
        22+ integrations — both-store read + publish APIs, per-locale
        scrapers, geo-distributed proxies, LLM-surface probing, encrypted
        credential vault, cost-compressing caches
        <SupLink to="platform-depth">B</SupLink>. 18+ months of patient
        engineering a competitor must repeat
        <SupLink to="capital-efficiency">D</SupLink>, against a target that
        keeps moving.
      </>
    ),
  },
  {
    num: "3",
    title: "A Rare Founder Combination",
    strap: "Deep ASO practice × Netflix-rooted product craft.",
    body: "A 12-year ASO practitioner who authored the methodology, paired with a product CEO from Netflix streaming, YouTube, and MessageBird's $800M Series C. The pairing produces what can't be bolted on — seeing market shifts early and shipping the response in weeks.",
    proof:
      "Recent proof: competitor's AI-discovery webinar → AI Discoverability Engine live in 8 days; enterprise-eval feature ask → shipped in 10.",
  },
];

const EXPANDING_MOATS: Moat[] = [
  {
    num: "4",
    title: "The Audit Corpus",
    strap: "Every audit makes the next audit better.",
    body: (
      <>
        <span className="font-semibold text-slate-900">
          ~24,000 customer-project audits since February 2026, across 200+
          apps
        </span>{" "}
        — scoring and recommendation history that can't be bought, only
        accrued. As customer publishing ramps, each shipped revision adds its
        measured lift to the corpus.
      </>
    ),
  },
  {
    num: "5",
    title: "Customer Lock-In",
    strap: "Switching costs compound with tenure.",
    body: "Configured competitor sets, calibrated rubrics, trained workflows — and the measured-lift history that makes each next recommendation credible. Leaving means restarting attribution from zero.",
  },
];

export default function MoatTechEdge() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader eyebrow="Moat">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Apptonomy is hard to replicate today —{" "}
          <br className="hidden md:block" />
          and it gets harder every week.
        </h1>
      </SlideHeader>

      <div className="flex flex-col gap-5 md:flex-1 md:justify-center md:gap-6">
        <MoatBand
          label="Now"
          sublabel="Already hard to replicate"
          accent="blue"
          moats={NOW_MOATS}
        />
        <MoatBand
          label="Expanding Moat Over Time"
          sublabel="Compounds with usage & tenure"
          accent="violet"
          moats={EXPANDING_MOATS}
        />
      </div>
    </div>
  );
}

function MoatBand({
  label,
  sublabel,
  accent,
  moats,
}: {
  label: string;
  sublabel: string;
  accent: Accent;
  moats: Moat[];
}) {
  const labelColor = accent === "blue" ? "text-blue-700" : "text-violet-700";
  const gridCols = moats.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <div>
      <div className="mb-3 flex items-baseline gap-3 md:mb-4">
        <p
          className={`text-xs font-bold uppercase tracking-[0.2em] md:text-sm ${labelColor}`}
        >
          {label}
        </p>
        <p className="text-xs italic text-slate-500 md:text-sm">{sublabel}</p>
        <div className="hidden h-px flex-1 self-center bg-slate-200 md:block" />
      </div>
      <div className={`grid grid-cols-1 gap-3 md:gap-4 ${gridCols}`}>
        {moats.map((m) => (
          <MoatCard key={m.num} moat={m} accent={accent} />
        ))}
      </div>
    </div>
  );
}

function MoatCard({ moat, accent }: { moat: Moat; accent: Accent }) {
  const badgeColor = accent === "blue" ? "bg-blue-600" : "bg-violet-600";
  const titleColor = accent === "blue" ? "text-blue-700" : "text-violet-700";
  return (
    <div className="flex flex-col rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-5">
      <div className="flex items-center gap-2.5">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${badgeColor}`}
        >
          {moat.num}
        </span>
        <h3
          className={`text-lg font-bold leading-tight tracking-tight md:text-xl ${titleColor}`}
        >
          {moat.title}
        </h3>
      </div>
      <p className="mt-2.5 text-sm font-semibold leading-snug text-slate-900 md:text-base">
        {moat.strap}
      </p>
      <p className="mt-2 text-xs leading-snug text-slate-600 md:text-sm">
        {moat.body}
      </p>
      {moat.proof ? (
        <p className="mt-2 text-xs italic leading-snug text-blue-800 md:text-sm">
          {moat.proof}
        </p>
      ) : null}
    </div>
  );
}
